import { message } from 'antd';
import axios, { AxiosError, AxiosResponse } from 'axios';
import AppConsts from '../lib/appconst';
import { endSession, isSessionEnding } from '../lib/session';
import { isTokenExpired } from '../utils/tokenUtils';

declare const abp: any;

const http = axios.create({
  baseURL: AppConsts.remoteServiceBaseUrl,
  timeout: 30000,
});

http.interceptors.request.use(
  function (config) {
    if (abp.auth.getToken()) {
      config.headers.Authorization = `Bearer ${abp.auth.getToken()}`;
    }

    config.headers['.AspNetCore.Culture'] = abp.utils.getCookieValue(
      'ZeroG33K.Localization.CultureName'
    );
    config.headers['ZeroG33K-TenantId'] = abp.multiTenancy.getTenantIdCookie();

    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

http.interceptors.response.use(
  async (response) => {
    return response;
  },
  (error: AxiosError) => {
    // The session is already ending and the page is on its way to the login screen. Every other
    // request in flight is about to fail too, and none of them has anything to add.
    if (isSessionEnding()) {
      return Promise.reject(error);
    }

    // We aborted it ourselves - a photo removed while it was still uploading, a screen left. Nothing
    // went wrong and nobody needs telling.
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }

    if (!error?.response) {
      // No response to read. A dropped connection looks exactly like a rejected one the browser
      // refuses to show us - a 401 whose response carried no CORS headers - so the token is what
      // tells the two apart. Only for someone who was signed in: a visitor on a public page has
      // no session to lose and must not be told one expired because their connection dropped.
      if (abp.session.userId && isTokenExpired()) {
        endSession('expired');
      } else {
        message.error({ content: 'Network error. Please try again.' });
      }
      return Promise.reject(error);
    }

    const { data, status } = error.response as AxiosResponse;
    switch (status) {
      case 400: {
        const fieldErrors = data?.error?.validationErrors
          ?.map((v: any) => v?.message)
          ?.filter(Boolean)
          ?.join('; ');
        message.error({ content: fieldErrors || data?.error?.message || 'Request failed.' });
        break;
      }
      case 401:
        endSession('unauthorized');
        break;
      case 403:
        message.error({ content: data?.error?.message || 'You are not allowed to do that.' });
        break;
      case 404:
        window.location.href = '/not-found';
        break;
      case 413:
        // Rejected by the server before it read a byte of the body. Left unhandled this failed
        // silently, which is how a save could look like it worked and not have.
        message.error({ content: 'That file is too large to upload. Please try a smaller one.' });
        break;
      case 500:
        message.error({ content: data?.error?.message || 'Something went wrong. Please try again.' });
        break;
    }
    return Promise.reject(error);
  }
);

export default http;
